import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import type { IqcStatus } from "./inboundApi";
import { cn } from "@/lib/utils";

export const INBOUND_IQC_OPTIONS: Array<{ value: IqcStatus; label: string }> = [
  { value: "pending", label: "Pending" },
  { value: "passed", label: "Passed" },
  { value: "failed", label: "Failed" },
  { value: "skipped", label: "Skipped" },
];

export function inboundIqcChipClasses(status: IqcStatus): string {
  if (status === "passed") return "bg-emerald-50 text-emerald-700 ring-emerald-300";
  if (status === "failed") return "bg-rose-50 text-rose-700 ring-rose-300";
  if (status === "skipped") return "bg-slate-50 text-slate-600 ring-slate-300";
  return "bg-amber-50 text-amber-700 ring-amber-300";
}

function labelForIqcStatus(status: IqcStatus): string {
  return INBOUND_IQC_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

export function InboundIqcStatusPicker({
  value,
  onChange,
  skuLabel,
  menuOpensUp = false,
}: {
  value: IqcStatus;
  onChange: (next: IqcStatus) => void;
  skuLabel: string;
  /** Open upward when the control sits near the bottom of a panel. */
  menuOpensUp?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`IQC status for ${skuLabel}: ${labelForIqcStatus(value)}`}
        className={cn(
          "inline-flex h-11 min-w-[6.75rem] items-center justify-between gap-1 rounded-md border border-slate-300 px-2.5 text-xs font-semibold uppercase tracking-wide ring-1 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
          inboundIqcChipClasses(value),
        )}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{labelForIqcStatus(value)}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label={`IQC status options for ${skuLabel}`}
          className={cn(
            "absolute left-0 z-30 min-w-full overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg",
            menuOpensUp ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          {INBOUND_IQC_OPTIONS.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                className={cn(
                  "flex h-11 w-full items-center justify-between gap-2 px-3 text-left text-sm font-semibold uppercase tracking-wide hover:brightness-95",
                  inboundIqcChipClasses(option.value),
                  selected ? "ring-2 ring-inset ring-brand-500" : "ring-1 ring-inset ring-transparent",
                )}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span>{option.label}</span>
                {selected ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
